/* eslint-disable @typescript-eslint/ban-ts-ignore */

import React from 'react';
import { uid } from 'react-uid';

import { isEqual } from 'lodash';

import { isJssField, JssTextType } from 'core/utils/sitecore-utils';
import Label from 'pods/forms/components/base/Label';
import ISharedProps from 'pods/forms/components/formFieldProps.d';
import UpdatedCtrlHolder from 'pods/forms/components/structural/UpdatedCtrlHolder';

import * as S from './RadioCheckbox.style';

// ----------------------------------------
// Raw form control exports

interface IRawRadioCheckboxListProps {
	type?: 'radio' | 'checkbox' | 'switch';
	name?: string;
	isSwitch?: boolean;
	isHorizontal?: boolean;
	disabled?: boolean;
	required?: boolean;
	hasError?: boolean;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	options?: {
		label: string | JssTextType;
		value: string;
		disabled?: boolean;
		checked?: boolean;
	}[];
	children: React.ReactNode;
	hasBorderline?: boolean;
	isLabelHidden?: boolean;
}

export const RawRadioCheckboxList = ({
	type = 'checkbox',
	name,
	isSwitch = false,
	isHorizontal = false,
	hasError = false,
	required = false,
	disabled = false,
	onChange,
	options = [],
	hasBorderline = false,
	children,
	isLabelHidden = false,
}: IRawRadioCheckboxListProps) => (
	<S.RadioCheckboxList {...{ hasError, isSwitch, isHorizontal, hasBorderline }}>
		{options.map(option => {
			const { value, label, disabled: optionDisabled, checked } = option;
			const optionId = uid(option);

			return (
				<RawRadioCheckbox
					key={optionId}
					id={optionId}
					disabled={disabled || optionDisabled}
					isLabelHidden={isLabelHidden}
					{...{ onChange, value, label, name, type, required, checked }}
				/>
			);
		})}
		{children}
	</S.RadioCheckboxList>
);

// ----------------------------------------

interface IRawRadioCheckboxProps {
	id: string;
	name?: string;
	type: 'radio' | 'checkbox' | 'switch';
	as?: string;
	value: string;
	label?: string | JssTextType;
	checked?: boolean;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: any;
	onKeyPress?: any;
	required?: boolean;
	disabled?: boolean;
	ariaLabel?: string;
	hasError?: boolean;
	hasBorderline?: boolean;
	isLabelHidden?: boolean;
}

export const RawRadioCheckbox = ({
	as: scAs,
	type = 'checkbox',
	label,
	ariaLabel,
	hasError = false,
	hasBorderline = false,
	isLabelHidden = false,
	...props
}: IRawRadioCheckboxProps) => {
	const customAriaLabel: string =
		// @ts-ignore: isJssField() already makes sure field is not undefined and has .value
		ariaLabel ?? isJssField(label) ? label.value : label;

	return (
		<S.RadioCheckboxItem
			hasError={hasError}
			hasBorderline={hasBorderline}
			as={scAs as any}
		>
			<input
				type={type === 'checkbox' ? 'checkbox' : 'radio'}
				{...(props as any)}
				{...(!!customAriaLabel && { 'aria-label': customAriaLabel })}
			/>
			<Label
				htmlId={props.id}
				label={label}
				isLabelHidden={isLabelHidden}
				{...(type === 'checkbox' && { withoutTextWrap: true })}
				{...(type === 'radio' && { margin: '0' })}
			/>
		</S.RadioCheckboxItem>
	);
};
// ----------------------------------------

export interface IProps extends ISharedProps {
	type: 'radio' | 'checkbox' | 'switch';
}

const RadioCheckbox = ({
	type,
	config,
	computed,
	htmlId,
	path,
	handleBlur,
	handleKeyPressFast,
	handleChange,
	value,
	getDisplayError,
	renderTextNode,
	ctrlHolderProps,
}: IProps) => {
	const { data = {} } = config;
	const { disabled, required } = computed;
	const { labelHelpText, horizontal, margin, options = [] } = data;

	const label = isJssField(data.label)
		? data.label
		: renderTextNode(data.label, 'span');
	const helpText = isJssField(labelHelpText)
		? labelHelpText
		: renderTextNode(labelHelpText, 'span');
	const tooltip = renderTextNode(data.tooltip);
	const errorMsg = getDisplayError();
	const _ctrlHolderProps = {
		htmlId,
		label,
		helpText,
		errorMsg,
		required,
		value,
		margin,
		...(data.tooltip && { tooltip }),
		...ctrlHolderProps,
	};

	// ----------------------------------------

	const _mapArrayOptions = (newValue: string, checked: boolean) => {
		let submitValues: string | string[];

		if (type === 'checkbox') {
			submitValues = value ? [].concat(value) : [];

			if (checked) {
				submitValues.push(newValue);
			} else {
				submitValues.splice(submitValues.indexOf(newValue), 1);
			}
		} else {
			submitValues = [newValue];
		}

		if (submitValues.length === 1) {
			if (
				type === 'radio' ||
				type === 'switch' ||
				(type === 'checkbox' && options.length === 1)
			) {
				submitValues = submitValues[0];
			}
		} else if (submitValues.length === 0) {
			// Keep 'empty'/null values type consistent for checkbox/radio respectively
			// i.e. all checkbox output types are arrays, and radio - strings
			submitValues = type === 'checkbox' ? [] : '';
		}

		return submitValues;
	};

	const _handleInputOnChange = (value: string, checked: boolean) => {
		const submitValues = _mapArrayOptions(value, checked);

		handleChange(submitValues);
	};

	const _checkOptionChecked = (fieldValue: string) =>
		Array.isArray(value)
			? value.includes(fieldValue)
			: isEqual(fieldValue, value);

	// ----------------------------------------

	return (
		<UpdatedCtrlHolder {..._ctrlHolderProps}>
			<RawRadioCheckboxList
				isSwitch={type === 'switch'}
				isHorizontal={horizontal}
				disabled={disabled}
			>
				{options.map(
					(option: { label: string; value: string; disabled: boolean }) => {
						const { label, value, disabled: optionDisabled } = option;
						const id = uid(option);

						return (
							<RawRadioCheckbox
								id={id}
								name={path}
								key={id}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									_handleInputOnChange(value, e.target.checked)
								}
								checked={_checkOptionChecked(value)}
								onBlur={handleBlur}
								onKeyPress={handleKeyPressFast}
								disabled={disabled ?? optionDisabled}
								{...{ label, type, value, required }}
							/>
						);
					},
				)}
			</RawRadioCheckboxList>
		</UpdatedCtrlHolder>
	);
};

export default RadioCheckbox;
